<template>
  <b-button
    :disabled="!canExport || isExporting"
    :variant="buttonVariant"
    :size="buttonSize"
    class="export-button"
    @click="handleExport"
  >
    <div class="button-content">
      <b-spinner
        v-if="isExporting"
        small
        class="mr-2"
      />
      <feather-icon
        v-else
        :icon="buttonIcon"
        size="16"
        class="mr-2"
      />
      <span class="button-text">{{ buttonText }}</span>
    </div>
  </b-button>
</template>

<script>
import { BButton, BSpinner } from 'bootstrap-vue'
import { computed } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'

export default {
  name: 'ExportButton',
  components: {
    BButton,
    BSpinner,
  },
  props: {
    demandType: {
      type: Number,
      default: 0,
    },
    size: {
      type: String,
      default: 'md',
    },
  },
  setup(props) {
    const toast = useToast()

    const isExporting = computed(() => store.getters['chart/isExporting'])
    const period = computed(() => store.getters['chart/getPeriod'])

    const canExport = computed(() => period.value.start && period.value.end)

    const buttonVariant = computed(() => {
      if (!canExport.value) { return 'outline-secondary' }
      if (isExporting.value) { return 'secondary' }
      return 'primary'
    })

    const buttonSize = computed(() => props.size)

    const buttonIcon = computed(() => {
      if (!canExport.value) { return 'AlertCircleIcon' }
      return 'DownloadIcon'
    })

    const buttonText = computed(() => {
      if (isExporting.value) { return 'Export en cours...' }
      if (!canExport.value) { return 'Sélectionner une période' }
      return 'Exporter Excel'
    })

    const displayToast = (title, icon, variant) => {
      toast({
        component: ToastificationContent,
        props: {
          title,
          icon,
          variant,
        },
      })
    }

    const handleExport = async () => {
      if (!canExport.value) {
        displayToast(
          'Veuillez sélectionner une période avant d\'exporter',
          'AlertCircleIcon',
          'warning',
        )
        return
      }

      try {
        const result = await store.dispatch('chart/exportYoungStatistics', {
          demandType: props.demandType,
        })

        if (result.success) {
          displayToast(
            'Export réalisé avec succès',
            'CheckIcon',
            'success',
          )
        } else {
          throw new Error('Erreur lors de l\'export')
        }
      } catch (error) {
        console.error('Erreur export:', error)
        displayToast(
          'Erreur lors de l\'export des données',
          'XIcon',
          'danger',
        )
      }
    }

    return {
      isExporting,
      canExport,
      buttonVariant,
      buttonSize,
      buttonIcon,
      buttonText,
      handleExport,
    }
  },
}
</script>

<style lang="scss" scoped>
.export-button {
  min-width: 180px;
  border-radius: 6px;
  font-weight: 500;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;

  &:not(:disabled) {
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    }

    &:active {
      transform: translateY(0);
    }
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &.btn-warning {
    background: linear-gradient(135deg, #43ff6c 0%, #13af35 100%);
    border: none;

    &:hover:not(:disabled) {
      background: linear-gradient(135deg, #43ff6c 0%, #13af35 100%);
    }
  }

  &.btn-outline-secondary {
    border: 2px dashed #adb5bd;
    color: #6c757d;
    background: transparent;

    &:hover {
      border-color: #6c757d;
      background: #f8f9fa;
      transform: none;
      box-shadow: none;
    }
  }
}

.button-content {
  display: flex;
  align-items: center;
  justify-content: center;
}

.button-text {
  font-size: 0.875rem;
  white-space: nowrap;
}

// Animation pour le spinner
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
